import * as React from 'react'
import { useLiquidityAdNodes } from './useLiquidityAdNodes';
import { LnRouterTableDefinition } from '../node/channelList/table/data/LnRouterTableDefinition';
import { ILiquidityAdNode } from './ILiquidityAdNode';
import { useUserPlan } from '../auth/plans/useUserPlan';
import { LnRouterTableColumn } from '../node/channelList/table/data/LnRouterTableColumn';
import Link from 'next/link';
import NodePubkey from '../general/NodePubkey';
import InternaLiquidityAdsTable from './InternalLiquidityAdsTable';
import FormatSatoshi from '../general/FormatSatoshi';
import FormatedNumber from '../general/FormatedNumber';
import ActionButton from '../general/ActionButton';
import LoadingSpinner from '../general/LoadingSpinner';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useWindowSize } from '../general/useWindowSize';
import GoldLockProtectionIcon from '../node/channelList/GoldLockProtectionIcon';


type Props = {
  amountSat: number,
  filters?: Map<string, number>
}

function useNodeTable(): [() => any, LnRouterTableDefinition<ILiquidityAdNode>] {
  const plan = useUserPlan();
  const isSubscriber = plan?.plan === 'SUBSCRIBED';
  const windowSize = useWindowSize()
  const isDesktop = windowSize.width > 800;




  function generateTable() {
    return new LnRouterTableDefinition<ILiquidityAdNode>([
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Node',
        'node.label',
        props => <div>
          <div className="font-semibold text-sm md:text-normal truncate"  style={{maxWidth: '10rem'}}>
            <Link href={'/node/' + props.row.original.node.id}>
              <a className='hover:text-viking-700' >
                {props.value}
              </a>
            </Link>
          </div>
          <div className="text-2xs text-gray-400">
            <NodePubkey nodeId={props.row.original.node.id} allowCopy={true} />
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left' }
      ),
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Capacity',
        'node.capacitySat',
        props => <div>
          <div className="text-sm">
            ₿<FormatSatoshi satoshi={props.value} decimals={2} />
          </div>
          <div className="text-2xs text-gray-500">
            {props.row.original.node.channelCount} channels
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Lease',
        'node.liquidityAd.leaseFeeBasis',
        props => <div className=''>
          <div className="">
            <span className="font-medium inline-block">{(props.value / 100).toFixed(2)}%</span> <span className="text-gray-500">+ <FormatedNumber value={props.row.original.node.liquidityAd.leaseFeeBaseMsat / 1000} />sat</span>
          </div>
          <div className='text-gray-400 text-2xs'>
            + {props.row.original.node.liquidityAd.fundingWeight / 4} vbytes onchain
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Maximum Routing Fees',
        'node.liquidityAd.channelFeeMaxProportionalThousandths',
        props => <div>
          <div className="text-gray-500 text-sm">
            <span className='text-black'><FormatedNumber value={props.value * 1000} />ppm</span> + <FormatedNumber value={props.row.original.node.liquidityAd.channelFeeMaxBaseMsat / 1000} />sat
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Onchain Funding',
        'node.liquidityAd.fundingWeight',
        props => <div className='text-right'>
          <div className="text-gray-500 text-xs">
            <FormatedNumber value={props.value / 4} /> vbytes
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'right', enabled: false }
      ),
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Routing Score',
        'node.liquidity.routingScorePercentile',
        props => <div className='text-center '>
          {isSubscriber &&
                    <div className="text-sm">
                    {props.value !== null && props.value !== undefined &&
                    <span>{props.value}th</span>
                    }
                  </div>
          }
          {!isSubscriber &&
            <GoldLockProtectionIcon />
          }

        </div>,
        { headerGroup: '', headerAlign: 'right', enabled: isDesktop }
      ),
      new LnRouterTableColumn<ILiquidityAdNode>(
        'Channel Open Fee',
        'openFeeSat',
        props => <div className='text-right'>
          <div className="font-medium text-lg">
            <FormatedNumber value={props.value} />sat
          </div>
        </div>,
        {
          headerGroup: '',
          headerComponent: column => <div className='px-2 hover:text-viking-800 text-right'>
            {column.label} <ArrowDownwardIcon fontSize="inherit" color="inherit" className="text-viking-700" />
          </div>
        }
      ),
    ])
  }

  const [table, setTable] = React.useState(generateTable());

  function forceUpdate() {
    setTable(table.copy());
  }

  React.useEffect(() => {
    setTable(generateTable())
  }, [isSubscriber])

  React.useEffect(() => {
    setTable(generateTable())
  }, [windowSize?.width])

  return [forceUpdate, table];
}


const LiquidityAdsTable: React.FC<Props> = ({ amountSat, filters = new Map<string, number>() }: Props) => {
  const limit = 25;
  const [forceUpdate, table] = useNodeTable();
  const [debouncedAmountSat, setDebouncedAmountSat] = React.useState<number>(amountSat);
  const [skip, setSkip] = React.useState<number>(0);
  const query = useLiquidityAdNodes(debouncedAmountSat, 1, 0, limit, filters);
  const elements = query.data?.elements || [];
  const rowsCount = query.data?.count || 0;

  

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSkip(0); // Reset skip on amountSat change
      setDebouncedAmountSat(amountSat);
    }, 1000)

    return () => clearTimeout(delayDebounceFn)

  }, [amountSat]);

  React.useEffect(() => {
    setSkip(0); // Reset skip on amountSat change
  }, [filters]);

  function loadMore() {
    const newSkip = skip + limit;
    setSkip(newSkip);
    query.fetchMore({
      variables: {
        skip: newSkip,
        limit: limit
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previous = previousResult.LiquidityAdNodes;
        const more = fetchMoreResult.LiquidityAdNodes;
        return {
          LiquidityAdNodes: {
            count: more.count,
            limit: previous.limit + more.limit,
            skip: 0,
            elements: [...previous.elements, ...more.elements]
          }
        };
      },
    })
  }

  const loading = query.loading || amountSat !== debouncedAmountSat;


  return (
    <div>
      <div className="flex justify-end pb-4">
        {loading &&
          <LoadingSpinner width={40} height={20} />
        }
        {!loading &&
          <div style={{ height: '20px' }} />
        }

      </div>
      <InternaLiquidityAdsTable
        className='w-full'
        definition={table} rows={elements} skip={skip} limit={limit} rowsCount={rowsCount}
        onUserInteraction={function (skip: number, limit: number) {
          loadMore();
        }} />
      {elements.length < rowsCount &&
        <div className='pt-8'>
          <ActionButton className="w-full" onClick={() => {
            loadMore();
          }}>
            Load more
          </ActionButton>
        </div>
      }
    </div>

  );
}

export default LiquidityAdsTable;

